import React, { useEffect, useRef, useState } from 'react';

const ParticleGrid = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Particle class
    class Particle {
      constructor(x, y) {
        this.originX = x;
        this.originY = y;
        this.x = x;
        this.y = y;
        this.vx = 0;
        this.vy = 0;
        this.radius = 2;
        this.friction = 0.7;
        this.springForce = 0.01;
        this.mouseRadius = 100;
        this.pushForce = 0.8;
      }

      update() {
        // Calculate distance from mouse
        const dx = this.x - mouseRef.current.x;
        const dy = this.y - mouseRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Apply mouse attraction with falloff
        if (distance < this.mouseRadius) {
          const force = (1 - distance / this.mouseRadius) * this.pushForce;
          const angle = Math.atan2(dy, dx);
          this.vx -= Math.cos(angle) * force;
          this.vy -= Math.sin(angle) * force;
        }
        
        // Spring force back to origin
        const springDx = this.originX - this.x;
        const springDy = this.originY - this.y;
        this.vx += springDx * this.springForce;
        this.vy += springDy * this.springForce;
        
        // Apply friction
        this.vx *= this.friction;
        this.vy *= this.friction;
        
        // Update position
        this.x += this.vx;
        this.y += this.vy;
      }

      draw() {
        // Calculate distance from origin for color effect
        const distFromOrigin = Math.sqrt(
          Math.pow(this.x - this.originX, 2) + 
          Math.pow(this.y - this.originY, 2)
        );
        
        // Color based on displacement
        const intensity = Math.min(distFromOrigin * 3, 255);
        ctx.fillStyle = `rgb(${100 + intensity}, ${150 - intensity/2}, ${255 - intensity})`;
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize particle grid
    const initParticles = () => {
      particlesRef.current = [];
      const spacing = 20;
      const cols = Math.floor(canvas.width / spacing);
      const rows = Math.floor(canvas.height / spacing);
      
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing + spacing / 2;
          const y = j * spacing + spacing / 2;
          particlesRef.current.push(new Particle(x, y));
        }
      }
    };

    // Set canvas size
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Mouse move handler
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    const animate = () => {
      ctx.fillStyle = 'rgba(10, 10, 20, 0.9)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-gray-900">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 cursor-crosshair"
      />
      <div className="absolute top-4 left-4 text-white text-sm bg-black bg-opacity-50 p-3 rounded">
        <p className="font-semibold mb-1">Interactive Particle Grid</p>
        <p className="text-xs opacity-75">Move your mouse to displace particles</p>
      </div>
    </div>
  );
};

export default ParticleGrid;